import type { Lot } from '../../types/lot'

import { getOfflineDb } from '../dbFactory'
import type { LotListParams, LotRepo } from './LotRepo'

export class DexieLotRepo implements LotRepo {
  async list(params: LotListParams = {}): Promise<Lot[]> {
    const farmId = params.farmId?.trim() || undefined
    const productionUnitId = params.productionUnitId?.trim() || undefined
    const lotKey = params.lotKey?.trim() || undefined
    const stage = params.stage?.trim() || undefined

    const db = getOfflineDb()
    const lotsTable = db.lots

    let base: Lot[]

    if (farmId && productionUnitId && stage) {
      base = await lotsTable.where('[farmId+productionUnitId+stage]').equals([farmId, productionUnitId, stage]).toArray()
    } else if (farmId && productionUnitId) {
      base = await lotsTable.where('[farmId+productionUnitId]').equals([farmId, productionUnitId]).toArray()
    } else if (farmId && stage) {
      base = await lotsTable.where('[farmId+stage]').equals([farmId, stage]).toArray()
    } else if (farmId) {
      base = await lotsTable.where('farmId').equals(farmId).toArray()
    } else if (productionUnitId) {
      base = await lotsTable.where('productionUnitId').equals(productionUnitId).toArray()
    } else if (stage) {
      base = await lotsTable.where('stage').equals(stage).toArray()
    } else {
      base = await lotsTable.toArray()
    }

    // Apply remaining filters in-memory (substring searches cannot use indexes).
    const normalizedQuery = lotKey ? lotKey.toLowerCase() : ''
    return base.filter((lot) => {
      if (stage && lot.stage !== stage) return false
      if (farmId && lot.farmId !== farmId) return false
      if (productionUnitId && lot.productionUnitId !== productionUnitId) return false
      if (normalizedQuery && !lot.lotKey.toLowerCase().includes(normalizedQuery)) return false
      return true
    })
  }

  async getById(id: string): Promise<Lot | undefined> {
    return getOfflineDb().lots.get(id)
  }

  async upsert(lot: Lot): Promise<void> {
    await getOfflineDb().lots.put(lot)
  }

  async upsertMany(lots: Lot[]): Promise<void> {
    if (lots.length === 0) return
    await getOfflineDb().lots.bulkPut(lots)
  }

  async replaceAll(lots: Lot[]): Promise<void> {
    const db = getOfflineDb()
    await db.transaction('rw', db.lots, async () => {
      await db.lots.clear()
      if (lots.length > 0) {
        await db.lots.bulkPut(lots)
      }
    })
  }

  async delete(id: string): Promise<void> {
    await getOfflineDb().lots.delete(id)
  }

  async clear(): Promise<void> {
    await getOfflineDb().lots.clear()
  }
}

export const dexieLotRepo = new DexieLotRepo()
